import React, { useMemo, useState } from 'react';
import { Button, Modal, Select, Space, Table, Tag, Typography } from 'antd';
import { InfoCircleOutlined, FolderAddOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTender } from '../../../contexts/TenderContext';
import { useDevMode } from '@/contexts/DevModeContext';
import { useCanConfigureBoq } from '@/hooks/useCanConfigureBoq';
import { useItemLibrary } from '../../../hooks/useItemLibrary';
import { useRoadsAndSewersMarkupFactor } from '../../../hooks/useRoadsAndSewersMarkupFactor';
import { useBoqLatestVersion } from '../../../hooks/useBillOfQuantities';
import { flattenBoqSummaryLineItems } from '../../../utils/roadsAndSewersBoqSummaryComputer';
import { BcCalculationDetails, computeBcCalculation } from '../../common/BcCalculationDetails';
import type { BoqSummaryLineItem } from '../../../utils/groundworksBoqSummaryComputer';
import { COLORS, formatUnit, BASE_ATTACHMENT_OPTIONS } from '../../../utils/constants';
import { formatCurrency } from '../../../utils/calculations';
import { roundNearest } from '../../../utils/roundingUtils';
import NumberInput from '../../common/NumberInput';
import ExpandCollapseAllButton from '../../common/ExpandCollapseAllButton';
import {
  buildRoadsBillSummarySections,
  sumRoadsBillSummaryAmount,
  type RoadsSummaryFormulaRow as FormulaRow,
  type RoadsSummaryFormulaInfo as FormulaInfo,
  type RoadsSummaryValueEntry as ValueEntry,
  type RoadsSummaryRowNode as RowNode,
  type RoadsSummarySectionNode as SectionNode,
  type RoadsSummaryRow as SummaryRow,
} from '../../../utils/roadsSummaryCalculations';
import { useBillSummaryExtraItems, buildRootExtraRows, buildNestedExtraRows, type BillSummaryExtraRow } from '../../../hooks/useBillSummaryExtraItems';
import type { DynamicBillSummaryNode, DynamicBillSummaryItemNode, DynamicBillSummarySectionNode } from '../../../types/tender.types';

const { Text, Title } = Typography;

/** Renders the ", attached …" suffix the Base course row gets from the Base attachments dropdown (see `formatBaseAttachments`) as one highlighted chip per option on its own line below the description. `trailing` (e.g. the dev-mode info icon) stays beside the main text. */
const ATTACHED_MARKER = ', attached ';
const renderDescriptionText = (text: string, trailing?: React.ReactNode, qtyByName?: Record<string, string>): React.ReactNode => {
  const idx = text.indexOf(ATTACHED_MARKER);
  if (idx < 0) return <>{text}{trailing}</>;
  const attached = text.slice(idx + ATTACHED_MARKER.length).split(' - ');
  return (
    <>
      {text.slice(0, idx)}
      {trailing}
      {attached.map((name) => (
        <span
          key={name}
          style={{
            display: 'block',
            width: 'fit-content',
            marginTop: 3,
            padding: '0 6px',
            borderRadius: 4,
            background: '#f9f0ff',
            border: '1px solid #d3adf7',
            color: '#531dab',
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          + attached {name}
          {qtyByName?.[name] !== undefined && <span style={{ marginLeft: 6, fontWeight: 600 }}>· Qty {qtyByName[name]}</span>}
        </span>
      ))}
    </>
  );
};

const findExtraNode = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === 'section') {
      const found = findExtraNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

// Ad-hoc extra rows (useBillSummaryExtraItems.tsx). An extra item attaches
// either at the table root ("__root__" bucket), or directly onto a real
// top-level section's own `children` (that section's own `key` as bucket) —
// `RoadsSummaryRowNode` (leaf items) has no `children` field of its own, so
// nesting stops at one level, same as Foul/Storm Sewers. Since this table's
// real rows pivot Qty/Rate/Amount per-road (`values: Record<roadId,
// ValueEntry>`), an extra item instead carries a single flat qty/rate/amount
// of its own (`presetQty`/`presetRate`/`presetAmount`) — the Qty/Rate/Amount
// columns below special-case `isExtra` to read those instead of summing
// `values` across roads.
type ExtRoadsItemRow = RowNode & {
  isExtra: true;
  amountType?: 'number' | 'text';
  amountText?: string;
  presetQty?: number;
  presetRate?: number;
  presetAmount?: number;
  // Which extraItems bucket this row lives in ("__root__", or a real
  // section's own key) — the Actions column's Edit/Delete calls need this to
  // address the right bucket instead of assuming root.
  attachKey?: string;
};
type ExtRoadsSectionRow = Omit<SectionNode, 'children'> & { isExtra: true; children: ExtRoadsItemRow[] };
type ExtRoadsRow = ExtRoadsSectionRow | ExtRoadsItemRow;

const extraRowToRoadsItem = (row: BillSummaryExtraRow): ExtRoadsItemRow => ({
  key: row.key,
  description: row.description,
  unit: row.unit || '',
  rowType: 'item',
  values: {},
  isExtra: true,
  amountType: row.amountType,
  amountText: row.amountText,
  presetQty: row.quantity ? parseFloat(row.quantity) || undefined : undefined,
  presetRate: row.amountType === 'text' ? undefined : parseFloat(row.rate || '0') || undefined,
  presetAmount: row.amountType === 'text' ? undefined : parseFloat(row.amount || '0') || undefined,
  attachKey: row.attachKey,
});

const extraRowToRoadsRow = (row: BillSummaryExtraRow): ExtRoadsRow =>
  row.nodeType === 'section'
    ? {
        key: row.key,
        description: row.description,
        rowType: 'section-header',
        isExtra: true,
        children: (row.children ?? []).map(extraRowToRoadsItem),
      }
    : extraRowToRoadsItem(row);

const fmtQty = (n: number) => roundNearest(Math.abs(n)).toLocaleString('en-GB');

/** Every fixed section-header key `sections` can produce, in display order — used by callers to build an "Expand All" control. */
export const CARRIAGEWAY_SUMMARY_SECTION_KEYS: React.Key[] = [
  'sec-site-clearance',
  'sec-excavating-filling',
  'sec-carriageways',
  'sec-footpaths',
  'sec-kerbs',
  'sec-ironwork',
  'sec-white-lining',
  'sec-street-nameplates',
  'sec-tree-bollards',
  'sec-remedials',
  'sec-post-rail',
  'sec-armco',
  'sec-signage',
];

interface RoadsBillSummaryProps {
  showRateAmount?: boolean;
  showHeader?: boolean;
  noPadding?: boolean;
  expandedRowKeys?: React.Key[];
  onExpandedRowKeysChange?: (keys: React.Key[]) => void;
  stickyOffset?: number;
  /** Hide every row whose Qty sums to 0 across all roads (see
   * pruneZeroQuantityRoadsSections, roadsSummaryCalculations.ts). Defaults to true —
   * the /roads-sewers/review Bill Summary tab. `CarriagewaySummary.tsx` (the take-off
   * page's own Carriageway Summary tab) sets this false: an estimator entering data
   * there needs to see every possible line item, not just the ones already populated. */
  hideZeroQtyRows?: boolean;
}

const RoadsBillSummary: React.FC<RoadsBillSummaryProps> = ({
  showRateAmount = true,
  showHeader = true,
  noPadding = false,
  expandedRowKeys: controlledExpandedRowKeys,
  onExpandedRowKeysChange,
  stickyOffset,
  hideZeroQtyRows = true,
}) => {
  const { state, updateField } = useTender();
  const showDevMode = useDevMode();

  // Item library for rate lookups by evaluate code — needed whenever the Rate/Amount
  // columns render (showRateAmount), and also for the dev-only Evaluate Code column.
  const { data: itemLibraryData } = useItemLibrary(state.region || '', undefined, {
    enabled: showDevMode || showRateAmount,
  });
  // Same OH&P/Preliminaries markup every R&S Bill Summary applies to its own
  // Rate column now — baked into resourceCodeMap below so every downstream
  // lookup (Rate/Amount columns, section subtotals, footer via
  // sumRoadsBillSummaryAmount) picks it up for free.
  const markupFactor = useRoadsAndSewersMarkupFactor();
  // Rate is rounded to 2dp here — the SAME value shown in the Rate column —
  // so Amount always equals the displayed Rate × displayed Qty exactly (the
  // raw item-library rate can carry more than 2dp, which would otherwise
  // silently drift Amount away from what the user can verify by hand). Same
  // convention every groundworks Bill Summary's Amount column uses.
  const resourceCodeMap = useMemo(() => {
    const map = new Map<string, number>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, parseFloat(((item.nettRate || item.baseRate || 0) * markupFactor).toFixed(2)));
    });
    return map;
  }, [itemLibraryData, markupFactor]);
  // A few Roads evaluate codes are combined (e.g. "Tree pits" →
  // "ALP200+ALP201") — same "+"-combined convention FoulBuildUpBillSummary.tsx/
  // StormBuildUpBillSummary.tsx already use; sum whichever parts resolve to a
  // library rate. Previously missing here, so a combined-code row read as
  // unpriced (blank Rate/Amount) on this bill even though every part exists
  // in the library.
  const getLibRate = (code?: string): number | undefined => {
    if (!code) return undefined;
    const trimmed = code.trim().toUpperCase();
    if (trimmed.includes('+')) {
      const parts = trimmed.split('+').map((c) => resourceCodeMap.get(c.trim())).filter((r): r is number => r !== undefined);
      return parts.length > 0 ? parts.reduce((sum, r) => sum + r, 0) : undefined;
    }
    return resourceCodeMap.get(trimmed);
  };
  // Plain nett rate, no markup — only for the dev-mode "BC" modal, which
  // shows the backend's dbRate-only recomputation for reconciliation.
  const nettResourceCodeMap = useMemo(() => {
    const map = new Map<string, number>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, parseFloat((item.nettRate || item.baseRate || 0).toFixed(2)));
    });
    return map;
  }, [itemLibraryData]);
  const [internalExpandedRowKeys, setInternalExpandedRowKeys] = useState<React.Key[]>([]);
  const expandedRowKeys = controlledExpandedRowKeys ?? internalExpandedRowKeys;
  const setExpandedRowKeys = onExpandedRowKeysChange ?? setInternalExpandedRowKeys;
  const [activeFormula, setActiveFormula] = useState<FormulaInfo | null>(null);
  // SummaryKey modal — "BCCalculation" independent backend-dbRate-only recomputation,
  // same as groundworks Bill Summaries (BcCalculationDetails.tsx / computeBcCalculation).
  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<BoqSummaryLineItem | null>(null);

  const roadsData = state.roads;
  // Quantities of the "attached" Preliminaries items (Binder & grading analysis / NDT's) —
  // looked up by item code in the saved Preliminary Items; shown beside each attached chip.
  const attachedQtyByName: Record<string, string> = {};
  BASE_ATTACHMENT_OPTIONS.forEach((o) => {
    const rec = (state.preliminaryItems?.preliminaryItems ?? []).find((p) => p.code === o.c);
    attachedQtyByName[o.n] = `${rec?.quantity ?? 0}${rec?.unit ? ` ${formatUnit(rec.unit)}` : ''}`;
  });
  const roads = roadsData?.carriagewayFootways.table || [];
  const manualQtys = roadsData?.manualQtys ?? {};
  const setManualQty = (key: string, value: number) => {
    if (!roadsData) return;
    updateField('roads', { ...roadsData, manualQtys: { ...manualQtys, [key]: value } });
  };

  // Builds one shared row per line item (fixed, road-invariant description/unit — mirrors
  // Main Services' single flat item list) with a per-road value entry in `row.values[road.id]`,
  // so roads render as columns instead of one accordion panel per road. Single source of
  // truth shared with `useRoadsAndSewersTotalSync.ts`'s roadsTotal — see roadsSummaryCalculations.ts.
  const sections = useMemo(
    () => buildRoadsBillSummarySections(roadsData, { pruneZeroQty: hideZeroQtyRows }),
    [roadsData, hideZeroQtyRows],
  );

  // Ad-hoc "Add Section"/"Add Item" extra rows — review page only
  // (showRateAmount=true; the take-off page's own per-road pivot view has no
  // Amount column for these to add into). See ExtRoadsRow above.
  const dynamicSummaryEnabled = useCanConfigureBoq() && showRateAmount;
  const roadsExtraItems = state.roadsExtraItems;
  const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
  const extraItemsApi = useBillSummaryExtraItems({
    extraItems: roadsExtraItems,
    onChange: (updated) => updateField('roadsExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      // `setExpandedRowKeys` may be `onExpandedRowKeysChange` (a plain external
      // callback, not a React state setter) when this component is used in
      // controlled mode — no functional-updater form here, unlike
      // FoulSewersBillSummary.tsx's own onNodeAdded, whose expandedRowKeys is
      // always its own internal useState.
      if (expandKey && !expandedRowKeys.includes(expandKey)) {
        setExpandedRowKeys([...expandedRowKeys, expandKey]);
      }
      setNewlyAddedRowId(nodeId);
      setTimeout(() => setNewlyAddedRowId((prev) => (prev === nodeId ? null : prev)), 2000);
    },
  });

  // Graft ad-hoc extra items directly onto a real top-level section's own
  // `children` (that section's own `key` as bucket) — same convention
  // groundworks/GroundworksCutFillBillSummary.tsx follows — in addition to
  // the existing root-level bucket ("__root__").
  const sectionsWithExtras = useMemo((): SummaryRow[] => {
    if (!showRateAmount) return sections;
    const buckets = roadsExtraItems ?? {};
    const injectedRealSections = sections.map((section) => {
      if (section.rowType !== 'section-header') return section;
      const bucket = buckets[section.key];
      if (!bucket || bucket.length === 0) return section;
      const extraRows = buildNestedExtraRows(bucket, section.key, section.key, section.children.length, markupFactor)
        .map(extraRowToRoadsItem);
      return { ...section, children: [...section.children, ...extraRows] };
    });
    const extraRootRows = buildRootExtraRows(buckets['__root__'] ?? [], '__root__', 1, markupFactor)
      .map(extraRowToRoadsRow);
    return [...injectedRealSections, ...(extraRootRows as SummaryRow[])];
  }, [sections, showRateAmount, roadsExtraItems, markupFactor]);

  const sumExtraRows = (rowsToSum: ExtRoadsRow[]): number =>
    rowsToSum.reduce((acc, row) => {
      if ('children' in row) return acc + sumExtraRows(row.children);
      if (row.amountType === 'text') return acc;
      return acc + (row.presetAmount ?? 0);
    }, 0);

  // Per-section extra-item subtotal (both grafted-onto-real-section and
  // extra-root-section buckets) — the Amount column's collapsed subtotal for
  // a real section adds this on top of `sectionTotals` (real rows only)
  // below, and `totalAmount` sums every value here too.
  const extraRowTotals = useMemo(() => {
    const map: Record<string, number> = {};
    const buckets = roadsExtraItems ?? {};
    sections.forEach((section) => {
      if (section.rowType !== 'section-header') return;
      const bucket = buckets[section.key];
      if (!bucket || bucket.length === 0) return;
      const extraRows = buildNestedExtraRows(bucket, section.key, section.key, section.children.length, markupFactor)
        .map(extraRowToRoadsItem);
      map[section.key] = sumExtraRows(extraRows);
    });
    const extraRootRows = buildRootExtraRows(buckets['__root__'] ?? [], '__root__', 1, markupFactor)
      .map(extraRowToRoadsRow);
    extraRootRows.forEach((row) => {
      if ('children' in row) {
        map[row.key] = sumExtraRows(row.children);
      } else {
        map[row.key] = row.amountType === 'text' ? 0 : (row.presetAmount ?? 0);
      }
    });
    return map;
  }, [sections, roadsExtraItems, markupFactor]);

  // Dev-mode only: pulls the persisted `roadAndSewers.summary.roads` tree from
  // the same boq-versions API the rest of this review page uses, so the
  // SummaryKey column reflects what was actually saved rather than the live
  // recomputation above.
  const { data: latestBoqVersionData } = useBoqLatestVersion(
    state.projectName || undefined,
    { enabled: showDevMode && !!state.projectName, oppNo: state.opportunityNo },
  );
  const persistedRoads = useMemo(
    () => flattenBoqSummaryLineItems(
      (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ roadAndSewers?: { summary?: { roads?: import('../../../utils/groundworksBoqSummaryComputer').BoqSummaryLineItem[] } } }> } })
        ?.boq?.billOfQuantities?.[0]?.roadAndSewers?.summary?.roads,
    ),
    [latestBoqVersionData],
  );

  // Per-section Amount subtotal — shown in the Amount column when a section-header
  // row is collapsed, same convention Foul/Storm Bill Summary's section/pipe-type
  // rows already use.
  const sectionTotals = useMemo(() => {
    const map: Record<string, number> = {};
    sections.forEach((section) => {
      let total = 0;
      section.children.forEach((row) => {
        if (row.rowType !== 'item' || !row.evaluateCode) return;
        const rate = getLibRate(row.evaluateCode);
        if (rate == null) return;
        const qty = Object.values(row.values).reduce((sum, v) => sum + (v.qty ?? 0), 0);
        total += qty * rate;
      });
      map[section.key] = total;
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, resourceCodeMap]);

  const columns = useMemo(() => {
    const roadCols = roads.map((road, idx) => ({
      title: road.roadName || `Road ${idx + 1}`,
      key: `road-${road.id}`,
      width: 140,
      align: 'right' as const,
      render: (_: unknown, r: SummaryRow) => {
        if (r.rowType !== 'item') return '';
        const v = r.values[road.id];
        if (!v) return '';
        if (v.naOverride) return <div style={{ textAlign: 'right' }}>N/A</div>;
        if (v.manualKey) {
          const key = v.manualKey;
          return (
            <NumberInput
              value={v.qty ?? 0}
              onChange={(val) => setManualQty(key, val)}
              decimalPlaces={0}
              isInteger
              positiveOnly
              size="small"
              style={{ width: '100%', textAlign: 'right' }}
              onClick={(e) => e.stopPropagation()}
            />
          );
        }
        return <span>{fmtQty(v.qty ?? 0)}</span>;
      },
    }));

    // One formula icon per item row (in Description) rather than one per road cell —
    // merges every road's breakdown rows (tagged with that road's name) into a single
    // modal, with a combined total matching the row's "Qty" column.
    const buildMergedFormulaInfo = (row: RowNode): FormulaInfo | null => {
      const entries = roads
        .map((road) => ({ road, v: row.values[road.id] }))
        .filter((e): e is { road: typeof roads[number]; v: ValueEntry & { formulaInfo: FormulaInfo } } => !!e.v?.formulaInfo);
      if (entries.length === 0) return null;
      const first = entries[0].v.formulaInfo;
      const rows: FormulaRow[] = entries.flatMap(({ road, v }) =>
        v.formulaInfo.rows.map((fr) => ({ ref: `${road.roadName || road.id} — ${fr.ref}`, value: fr.value, unit: fr.unit })),
      );
      const total = entries.reduce((sum, { v }) => sum + (v.formulaInfo.total ?? 0), 0);
      return { label: first.label, formula: first.formula, excelRef: first.excelRef, rows, total, unit: first.unit };
    };

    // /roads-sewers/review only: Qty sits right after Description (matching every other
    // bill summary's column order), and is never fixed since the review table has no
    // horizontal scroll at all (see tableScrollX below).
    const reviewQtyCol = {
      title: 'Qty',
      key: 'qty',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, r: SummaryRow) => {
        if (r.rowType !== 'item') return '';
        const extra = r as Partial<ExtRoadsItemRow>;
        if (extra.isExtra) {
          if (extra.amountType === 'text' || !dynamicSummaryEnabled || state.isViewMode) {
            return extra.presetQty != null ? <span>{fmtQty(extra.presetQty)}</span> : '';
          }
          return (
            <NumberInput
              value={extra.presetQty ?? 0}
              onChange={(qty) => extraItemsApi.updateNode(extra.attachKey ?? '__root__', r.key as string, { qty })}
              size="small"
              decimalPlaces={2}
              autoFocus={r.key === newlyAddedRowId}
            />
          );
        }
        const total = roads.reduce((sum, road) => sum + (r.values[road.id]?.qty ?? 0), 0);
        return <span style={{ fontWeight: 600 }}>{fmtQty(total)}</span>;
      },
    };

    // /roads-sewers/roads (CarriagewaySummary.tsx, showRateAmount=false) unchanged from
    // its original take-off layout: Description, Unit, [Evaluate Code], one column per
    // road, then Total (Qty) fixed to the right edge.
    if (!showRateAmount) {
      return [
        {
          title: 'Description',
          dataIndex: 'description',
          key: 'description',
          fixed: 'left' as const,
          width: 340,
          render: (v: string, r: SummaryRow) => {
            const wrapStyle: React.CSSProperties = {
              display: 'block',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              paddingLeft: r.rowType === 'section-header' ? 0 : 24,
            };
            if (r.rowType === 'section-header') return <Text strong className="text-indigo-600" style={{ ...wrapStyle, textTransform: 'uppercase' }}>{v}</Text>;
            if (r.rowType === 'sub-header') return <Text strong className="text-blue-700" style={{ ...wrapStyle, textTransform: 'uppercase' }}>{v}</Text>;
            const merged = buildMergedFormulaInfo(r as RowNode);
            return (
              <span style={wrapStyle}>
                {renderDescriptionText(
                  v,
                  showDevMode && merged && (
                  <InfoCircleOutlined
                    style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveFormula(merged);
                    }}
                  />
                  ),
                  attachedQtyByName,
                )}
              </span>
            );
          },
        },
        {
          title: 'Unit',
          dataIndex: 'unit',
          key: 'unit',
          width: 80,
          fixed: 'left' as const,
          align: 'center' as const,
          render: (v: string, r: SummaryRow) => (r.rowType === 'item' ? formatUnit(v) : ''),
        },
        ...(showDevMode ? [{
          title: 'Evaluate Code',
          key: 'evaluateCode',
          width: 140,
          fixed: 'left' as const,
          render: (_: unknown, r: SummaryRow) => {
            if (r.rowType !== 'item') return '';
            const code = (r as RowNode).evaluateCode;
            if (!code) return <Text type="secondary">—</Text>;
            const libRate = getLibRate(code);
            return (
              <span>
                <Tag color="blue">{code}</Tag>
                {libRate !== undefined && (
                  <div style={{ color: '#52c41a', fontSize: 12, marginTop: 2 }}>
                    {formatCurrency(libRate)}
                  </div>
                )}
              </span>
            );
          },
        }] : []),
        ...roadCols,
        {
          title: 'Total',
          key: 'total',
          width: 120,
          align: 'right' as const,
          fixed: 'right' as const,
          render: (_: unknown, r: SummaryRow) => {
            if (r.rowType !== 'item') return '';
            const total = roads.reduce((sum, road) => sum + (r.values[road.id]?.qty ?? 0), 0);
            return <span style={{ fontWeight: 600 }}>{fmtQty(total)}</span>;
          },
        },
      ];
    }

    // /roads-sewers/review: no per-road columns, Qty/Rate/Amount instead, plus the dev-mode
    // evaluate code as a sub-line under Description (PrelimsBillSummary.tsx's convention).
    return [
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: 340,
        render: (v: string, r: SummaryRow) => {
          const wrapStyle: React.CSSProperties = {
            display: 'block',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            paddingLeft: r.rowType === 'section-header' ? 0 : 24,
          };
          if (r.rowType === 'section-header') return <Text strong className="text-indigo-600" style={{ ...wrapStyle, textTransform: 'uppercase' }}>{v}</Text>;
          if (r.rowType === 'sub-header') return <Text strong className="text-blue-700" style={{ ...wrapStyle, textTransform: 'uppercase' }}>{v}</Text>;
          const merged = buildMergedFormulaInfo(r as RowNode);
          const code = (r as RowNode).evaluateCode;
          return (
            // paddingLeft on the outer container (not the inner span) so the
            // description text and the code sub-line share the same left edge —
            // putting it on the span alone left the sub-line 24px further left.
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingLeft: 24 }}>
              <span style={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {renderDescriptionText(
                  v,
                  showDevMode && merged && (
                  <InfoCircleOutlined
                    style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveFormula(merged);
                    }}
                  />
                  ),
                  attachedQtyByName,
                )}
              </span>
              {showDevMode && code && (
                <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2, fontFamily: 'monospace' }}>
                  {code}
                </div>
              )}
            </div>
          );
        },
      },
      reviewQtyCol,
      {
        title: 'Unit',
        dataIndex: 'unit',
        key: 'unit',
        width: 80,
        align: 'center' as const,
        render: (v: string, r: SummaryRow) => {
          if (r.rowType !== 'item') return '';
          const extra = r as Partial<ExtRoadsItemRow>;
          if (extra.isExtra && extra.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
            return (
              <Select
                value={v || undefined}
                onChange={(unit) => extraItemsApi.updateNode(extra.attachKey ?? '__root__', r.key as string, { unit })}
                options={extraItemsApi.unitOptions.map((u) => ({ value: u, label: u }))}
                placeholder="Unit"
                size="small"
                showSearch
                allowClear
                style={{ width: '100%' }}
              />
            );
          }
          return formatUnit(v) || '';
        },
      },
      {
        title: 'Rate',
        key: 'rate',
        width: 110,
        align: 'right' as const,
        render: (_: unknown, r: SummaryRow) => {
          if (r.rowType !== 'item') return '';
          const extra = r as Partial<ExtRoadsItemRow>;
          if (extra.isExtra) {
            return extra.presetRate != null ? <span>{formatCurrency(extra.presetRate)}</span> : <Text type="secondary">—</Text>;
          }
          const rate = getLibRate((r as RowNode).evaluateCode);
          return rate != null ? <span>{formatCurrency(rate)}</span> : <Text type="secondary">—</Text>;
        },
      },
      {
        title: 'Amount',
        key: 'amount',
        width: 120,
        align: 'right' as const,
        render: (_: unknown, r: SummaryRow) => {
          if (r.rowType === 'section-header') {
            const isCollapsed = !expandedRowKeys.includes(r.key);
            if (!isCollapsed) return '';
            const subtotal = (sectionTotals[r.key] ?? 0) + (extraRowTotals[r.key] ?? 0);
            return subtotal > 0 ? <span style={{ fontWeight: 700 }}>{formatCurrency(subtotal)}</span> : '';
          }
          if (r.rowType !== 'item') return '';
          const extra = r as Partial<ExtRoadsItemRow>;
          if (extra.isExtra) {
            if (extra.amountType === 'text') return <span>{extra.amountText}</span>;
            return extra.presetAmount != null ? <span style={{ fontWeight: 600 }}>{formatCurrency(extra.presetAmount)}</span> : <Text type="secondary">—</Text>;
          }
          const rate = getLibRate((r as RowNode).evaluateCode);
          if (rate == null) return <Text type="secondary">—</Text>;
          const qty = roads.reduce((sum, road) => sum + (r.values[road.id]?.qty ?? 0), 0);
          return <span style={{ fontWeight: 600 }}>{formatCurrency(qty * rate)}</span>;
        },
      },
      // ── Dev-mode only: persisted-vs-live comparison column ─────────────────
      ...(showDevMode ? [{
        title: 'SummaryKey',
        key: 'summaryKey',
        width: 120,
        align: 'right' as const,
        render: (_: unknown, r: SummaryRow) => {
          if (r.rowType !== 'item') return '';
          if ((r as Partial<ExtRoadsItemRow>).isExtra) return <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>;
          const persisted = persistedRoads.get(r.key as string);
          if (!persisted) return <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>;
          const rate = getLibRate((r as RowNode).evaluateCode);
          const qty = roads.reduce((sum, road) => sum + (r.values[road.id]?.qty ?? 0), 0);
          const liveAmount = rate != null ? qty * rate : 0;
          const mismatch = Math.abs(liveAmount - (persisted.amount || 0)) > 0.01;
          const bcAmount = computeBcCalculation(persisted, (code) => nettResourceCodeMap.get(code) ?? 0).amount;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : '#6b7280' }}>
                  {formatCurrency(persisted.amount || 0)}
                </Text>
                <InfoCircleOutlined
                  style={{ cursor: 'pointer', color: '#3b82f6' }}
                  onClick={() => setSummaryKeyModalRecord(persisted)}
                />
              </div>
              <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
                BC: {formatCurrency(bcAmount)}
              </Text>
            </div>
          );
        },
      }] : []),
      // ── Add Item on any real section-header row, Edit/Delete on extra rows
      // — a real section is immutable itself but can still host grafted
      // extra items (its own `key` becomes that bucket's attachKey). ──────
      ...(dynamicSummaryEnabled && !state.isViewMode ? [{
        title: 'Actions',
        key: 'extraActions',
        width: 150,
        align: 'center' as const,
        render: (_: unknown, r: SummaryRow) => {
          const extra = r as SummaryRow & { isExtra?: boolean; attachKey?: string };
          if (extra.isExtra) {
            const bucketKey = extra.attachKey ?? '__root__';
            const node = findExtraNode(roadsExtraItems?.[bucketKey] ?? [], r.key as string);
            if (!node) return '';
            if (r.rowType === 'section-header') {
              return (
                <Space size={4}>
                  <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(bucketKey, r.key as string)} />
                  <Button size="small" icon={<EditOutlined />} title="Edit Section" onClick={() => extraItemsApi.openEditSection(bucketKey, node as DynamicBillSummarySectionNode)} />
                  <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(bucketKey, r.key as string)} />
                </Space>
              );
            }
            return (
              <Space size={4}>
                <Button size="small" icon={<EditOutlined />} title="Edit Item" onClick={() => extraItemsApi.openEditItem(bucketKey, node as DynamicBillSummaryItemNode)} />
                <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(bucketKey, r.key as string)} />
              </Space>
            );
          }
          if (r.rowType === 'section-header') {
            return (
              <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(r.key as string, null)} />
            );
          }
          return '';
        },
      }] : []),
    ];
  }, [
    roads, showRateAmount, showDevMode, resourceCodeMap, expandedRowKeys, sectionTotals, persistedRoads,
    dynamicSummaryEnabled, state.isViewMode, roadsExtraItems, extraRowTotals, newlyAddedRowId,
    state.preliminaryItems?.preliminaryItems, // attachedQtyByName is derived from it
  ]);

  const totalAmount = useMemo(() => {
    if (!showRateAmount) return 0;
    const totalExtra = Object.values(extraRowTotals).reduce((sum, v) => sum + v, 0);
    return sumRoadsBillSummaryAmount(sections, getLibRate) + totalExtra;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, showRateAmount, resourceCodeMap, extraRowTotals]);

  // Horizontal scroll (and the `fixed` columns above) is only needed for the take-off's
  // own per-road editable view (CarriagewaySummary.tsx, showRateAmount=false), whose road
  // columns can add up to far more width than the viewport. The review-page bill
  // (showRateAmount=true) has a handful of fixed-width columns that always fit, so it
  // renders without a scroll container at all. Sticky headers rely on the body table using
  // `table-layout: fixed` (see column widths above) — a numeric scroll.x forces that;
  // 'max-content' lets rows auto-size instead, which desyncs the sticky header from the
  // body and mis-wraps long Description text.
  const tableScrollX = 340 + 80 + roads.length * 140 + 120 + (showDevMode ? 140 : 0);

  if (roads.length === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center' }}>
        <Text type="secondary">No roads configured. Add roads on the Carriageway &amp; Footways tab.</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: noPadding ? 0 : '16px' }}>
      {showHeader && (
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0, fontWeight: 600, fontSize: '16px', color: '#111827' }}>
            Carriageway Summary
          </Title>
          {/* Only the review page (showRateAmount=true) needs this here — the
              take-off page's own Expand/Collapse control already lives in
              RoadsTakeoff.tsx, external to this component, so rendering a
              second one here for that variant would duplicate it. */}
          {showRateAmount && (
            <Space>
              {dynamicSummaryEnabled && !state.isViewMode && (
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => extraItemsApi.openAddSection('__root__', null)}>
                  Add Section
                </Button>
              )}
              <ExpandCollapseAllButton
                expanded={expandedRowKeys.length > 0}
                onToggle={() => setExpandedRowKeys(
                  expandedRowKeys.length > 0 ? [] : CARRIAGEWAY_SUMMARY_SECTION_KEYS,
                )}
              />
            </Space>
          )}
        </div>
      )}

      <style>{`
        .cway-bill-table .ant-table { border-radius: 0 !important; }
        .cway-bill-table .ant-table-container { border-radius: 0 !important; }
        .cway-bill-table .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
          min-width: 90px;
        }
        /* Section-header rows now use the exact same 'bg-indigo-50
           font-semibold' Tailwind classes every other R&S Bill Summary table
           applies to its own top-level section rows (see rowClassName below)
           — previously a custom class with hand-picked hex colors that were
           meant to match but rendered visibly different from Tailwind's own
           bg-indigo-50. Only :hover needs an explicit rule here (same as
           every other R&S Bill Summary's own <style> block): the resting
           color comes from Tailwind's bg-indigo-50 utility on the <tr>
           itself, with no override needed. */
        .cway-bill-table .ant-table-tbody > tr:hover > td { background-color: #fafafa !important; }
        .cway-bill-table .ant-table-tbody > tr.bg-indigo-50:hover > td { background-color: #e0e7ff !important; }
        /* The take-off variant (showRateAmount=false, CarriagewaySummary.tsx)
           uses fixed:'left'/fixed:'right' columns (Description/Unit/Evaluate
           Code, and Total) — position:sticky cells that antd paints with
           their own opaque background (.ant-table-cell-fix-left/-right) to
           stay solid while the road columns scroll under them. That sticky
           background sits on top of the plain tr:hover rule above and never
           got its own :hover override, so those columns (most of the row's
           visible width) stayed white on hover — only the non-fixed road
           columns showed the grey tint. Same fix already used for this
           exact scenario elsewhere in this file's own take-off page — see
           RoadsTakeoff.tsx's .carriageway-cutfill-table rules. */
        .cway-bill-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left,
        .cway-bill-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-right {
          background-color: #fafafa !important;
        }
        .cway-bill-table .ant-table-tbody > tr.bg-indigo-50:hover > td.ant-table-cell-fix-left,
        .cway-bill-table .ant-table-tbody > tr.bg-indigo-50:hover > td.ant-table-cell-fix-right {
          background-color: #e0e7ff !important;
        }
      `}</style>

      <Table<SummaryRow>
        className="cway-bill-table"
        dataSource={sectionsWithExtras}
        columns={columns}
        rowKey="key"
        bordered
        size="middle"
        pagination={false}
        scroll={showRateAmount ? undefined : { x: tableScrollX }}
        sticky={stickyOffset !== undefined ? { offsetHeader: stickyOffset, offsetScroll: 9999 } : undefined}
        expandable={{
          childrenColumnName: 'children',
          expandedRowKeys,
          // Child rows (item/sub-header) have no expand icon, so antd's default
          // indent spacer is an inline sibling before the text — the text's first
          // line sits offset by it, but any WRAPPED line breaks back to the cell's
          // true left edge (an inline sibling's offset isn't inherited by wrapped
          // lines). indentSize: 0 removes that spacer; padding on the text block
          // itself (below) indents every line, wrapped or not, consistently.
          indentSize: 0,
          onExpand: (expanded, record) => {
            const k = record.key;
            setExpandedRowKeys(expanded ? [...expandedRowKeys, k] : expandedRowKeys.filter((x) => x !== k));
          },
        }}
        rowClassName={(r) => {
          // Same 'bg-indigo-50 font-semibold' Tailwind classes every other R&S
          // Bill Summary table uses for its own top-level section rows — was a
          // custom 'cway-row-section-header' class with hand-picked hex colors
          // (#eef2ff/#e0e7ff) that were meant to match but rendered visibly
          // different from Tailwind's own bg-indigo-50 the other tables use
          // for the identical row concept. Using the exact same class
          // guarantees identical colors, not just "the same-looking" hex.
          if (r.rowType === 'section-header') return 'bg-indigo-50 font-semibold';
          if ((r as Partial<ExtRoadsItemRow>).key === newlyAddedRowId) return 'roads-summary-row-added';
          return '';
        }}
        style={{ border: '1px solid #e5e7eb', borderRadius: 4 }}
        summary={showRateAmount ? () => (
          <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 700 }}>
            <Table.Summary.Cell index={0} colSpan={3}>
              <Text strong>TOTAL CARRIAGEWAY</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} />
            <Table.Summary.Cell index={2} align="right">
              <Text strong>{formatCurrency(totalAmount)}</Text>
            </Table.Summary.Cell>
            {showDevMode && <Table.Summary.Cell index={3} />}
            {dynamicSummaryEnabled && !state.isViewMode && <Table.Summary.Cell index={showDevMode ? 4 : 3} />}
          </Table.Summary.Row>
        ) : undefined}
      />
      <style>{`
        @keyframes roads-summary-row-added-fade { 0% { background-color: #fef3c7; } 100% { background-color: transparent; } }
        .roads-summary-row-added > td { animation: roads-summary-row-added-fade 1.8s ease-out; }
      `}</style>
      {extraItemsApi.modals}

      <Modal
        title="SummaryKey — Amount Calculation"
        open={!!summaryKeyModalRecord}
        onCancel={() => setSummaryKeyModalRecord(null)}
        footer={null}
      >
        {summaryKeyModalRecord && (
          <BcCalculationDetails
            record={summaryKeyModalRecord}
            getDbRate={(code) => nettResourceCodeMap.get(code) ?? 0}
          />
        )}
      </Modal>

      {/* Formula info modal — matches Foul Sewers' column-calculation-info modal */}
      {activeFormula && (
        <Modal
          title={`${activeFormula.label} — Calculation`}
          open={true}
          onCancel={() => setActiveFormula(null)}
          footer={[<Button key="close" onClick={() => setActiveFormula(null)}>Close</Button>]}
          width={500}
        >
          <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f4ff', borderRadius: 4, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
            {activeFormula.formula}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RoadsBillSummary;
